import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { message } from "antd";
import { BaseUrl } from "../../config/BaseUrl";

const normalizeImages = (images) => {
  if (Array.isArray(images)) return images.filter(Boolean);
  return images ? [images] : [];
};

const defaultFormState = {
  title: "",
  description: "",
  articleImage: [],
  status: true,
};

const ViralImages = () => {
  const [records, setRecords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultFormState);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [viewImages, setViewImages] = useState([]);
  const [updatingImages, setUpdatingImages] = useState(false);

  const API_BASE = BaseUrl || "";

  const loadViralImages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/v1/article/all`);
      const articleList = Array.isArray(response?.data?.articles)
        ? response.data.articles
        : [];

      setRecords(articleList);

    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to load viral images");
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    loadViralImages();
  }, [loadViralImages]);

  const resetForm = () => {
    setEditingId(null);
    setForm(defaultFormState);
  };

  const fillForm = (record) => {
    setEditingId(record._id);
    setForm({
      title: record.title || "",
      description: record.description || "",
      articleImage: normalizeImages(record.articleImage),
      status: record.status ?? true,
    });
  };

  const openImageViewer = (record) => {
    setViewingRecord(record);
    setViewImages(normalizeImages(record.articleImage));
  };

  const persistViewerImages = async (nextImages) => {
    if (!viewingRecord || nextImages.length === 0) {
      message.warning("At least one image is required.");
      return false;
    }

    try {
      setUpdatingImages(true);
      const payload = { articleImage: nextImages };
      const response = await axios.put(
        `${API_BASE}/api/v1/article/${viewingRecord._id}`,
        payload
      );
      const updatedRecord = response?.data?.article || { ...viewingRecord, ...payload };
      setViewingRecord(updatedRecord);
      setViewImages(nextImages);
      setRecords((current) => current.map((record) =>
        record._id === updatedRecord._id ? updatedRecord : record
      ));
      if (editingId === updatedRecord._id) {
        setForm((current) => ({
          ...current,
          articleImage: nextImages,
        }));
      }
      return true;
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to update images");
      return false;
    } finally {
      setUpdatingImages(false);
    }
  };

  const addViewerImages = async (files) => {
    if (!files.length || !viewingRecord) return;
    if (viewImages.length + files.length > 10) {
      message.warning("A maximum of 10 images is allowed.");
      return;
    }

    try {
      setUploading(true);
      const uploadedUrls = [];
      for (const file of files) {
        const reader = new FileReader();
        const dataUrl = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const response = await axios.post(`${API_BASE}/api/v1/article/artImg`, {
          image: dataUrl.replace(/^data:image\/[a-zA-Z0-9.+-]+;base64,/, ""),
        });
        const imageUrl = response?.data?.articleImage;
        if (!imageUrl) throw new Error("No image URL returned from API");
        uploadedUrls.push(imageUrl);
      }
      const saved = await persistViewerImages([...viewImages, ...uploadedUrls]);
      if (saved) message.success("Images added");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadImages = async (files) => {
    if (!files.length) return;
    if (form.articleImage.length + files.length > 10) {
      message.warning("A maximum of 10 images is allowed.");
      return;
    }

    try {
      setUploading(true);
      const uploadedUrls = [];
      for (const file of files) {
        const reader = new FileReader();
        const dataUrl = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const response = await axios.post(`${API_BASE}/api/v1/article/artImg`, {
          image: dataUrl.replace(/^data:image\/[a-zA-Z0-9.+-]+;base64,/, ""),
        });
        const imageUrl = response?.data?.articleImage || response?.data?.url || response?.data?.imageUrl;
        if (!imageUrl) throw new Error("No image URL returned from API");
        uploadedUrls.push(imageUrl);
      }

      setForm((prev) => ({ ...prev, articleImage: [...prev.articleImage, ...uploadedUrls] }));
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveRecord = async () => {
    try {
      const payload = {
        title: form.title || "Viral image",
        description: form.description || "",
        articleImage: form.articleImage,
        status: form.status,
      };

      if (payload.articleImage.length === 0) {
        message.warning("Please upload an image before saving.");
        return;
      }

      if (editingId) {
        await axios.put(`${API_BASE}/api/v1/article/${editingId}`, payload);
        message.success("Viral image updated successfully");
      } else {
        await axios.post(`${API_BASE}/api/v1/article/new`, payload);
        message.success("Viral image added successfully");
      }

      resetForm();
      loadViralImages();
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to save viral image");
    }
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(`${API_BASE}/api/v1/article/${recordId}`);
      message.success("Viral image removed");
      if (editingId === recordId) {
        resetForm();
      }
      loadViralImages();
    } catch (error) {
      message.error(error?.message || "Failed to remove viral image");
    }
  };

  return (
    <div className="p-2" style={{ minHeight: "auto", maxHeight: "calc(100vh - 130px)", overflow: "hidden" }}>
      <div className="profile_container_header">
        <div className="profile_co_header_left">
          <span>Viral Images</span>
        </div>
      </div>

      <div
        className="main_recha_container"
        style={{
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "16px",
        }}
      >
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", marginBottom: 20 }}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
            <h4 style={{ marginBottom: 12 }}>{editingId ? "Edit Viral Image" : "Add Viral Image"}</h4>

            <div style={{ display: "grid", gap: 12 }}>
              <TextField
                label="Title"
                size="small"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              />

              <TextField
                label="Description"
                size="small"
                multiline
                minRows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.status)}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked }))}
                    color="success"
                  />
                }
                label={form.status ? "Active" : "Inactive"}
              />

              <div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploading || form.articleImage.length >= 10}
                  onChange={(e) => {
                    uploadImages(Array.from(e.target.files || []));
                    e.target.value = "";
                  }}
                />
                <div style={{ color: "#666", fontSize: 12, marginTop: 5 }}>
                  {form.articleImage.length} / 10 images
                </div>
                {uploading && (
                  <span style={{ marginLeft: 8 }}>
                    <CircularProgress size={16} />
                  </span>
                )}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {form.articleImage.map((image, index) => (
                  <div key={`${image}-${index}`} style={{ position: "relative", width: 120, height: 100 }}>
                    <img
                      src={image}
                      alt={`Viral ${index + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4, border: "1px solid #ddd" }}
                    />
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      aria-label={`Remove image ${index + 1}`}
                      onClick={() => setForm((prev) => {
                        const articleImage = prev.articleImage.filter((_, imageIndex) => imageIndex !== index);
                        return { ...prev, articleImage };
                      })}
                      style={{ position: "absolute", top: 3, right: 3, minWidth: 24, padding: "1px 5px" }}
                    >
                      X
                    </Button>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="contained" color="primary" onClick={saveRecord} disabled={uploading}>
                  {editingId ? "Update" : "Add"}
                </Button>
                <Button variant="outlined" onClick={resetForm}>
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {records.length === 0 ? (
              <div style={{ color: "#666" }}>No viral images added yet.</div>
            ) : (
              records.map((record, index) => (
                <div
                  key={record._id || `${record.articleImage}-${index}`}
                  style={{ width: 240, border: "1px solid #ddd", borderRadius: 8, overflow: "hidden", background: "#fff" }}
                >
                  <div style={{ position: "relative" }}>
                    <img
                      src={normalizeImages(record.articleImage)[0]}
                      alt={`Viral ${index + 1}`}
                      style={{ width: "100%", height: 170, objectFit: "cover" }}
                    />
                    <Tooltip title="View images">
                      <IconButton
                        aria-label={`View images for ${record.title || "viral image"}`}
                        onClick={() => openImageViewer(record)}
                        sx={{ position: "absolute", top: 8, right: 8, bgcolor: "white", "&:hover": { bgcolor: "#f2f2f2" } }}
                      >
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </div>

                  <div style={{ padding: 10, display: "grid", gap: 8 }}>
                    <div><strong>{record.title || "Viral image"}</strong></div>
                    <div style={{ color: "#666", fontSize: 12 }}>{record.description || "-"}</div>
                    <div style={{ color: record.status === false ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}>
                      {record.status === false ? "Inactive" : "Active"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {normalizeImages(record.articleImage).slice(0, 3).map((image, imageIndex) => (
                        <img
                          key={`${image}-${imageIndex}`}
                          src={image}
                          alt={`Viral thumbnail ${imageIndex + 1}`}
                          style={{ width: 42, height: 34, objectFit: "cover", borderRadius: 3, border: "1px solid #ddd" }}
                        />
                      ))}
                      <span style={{ color: "#666", fontSize: 12 }}>
                        {normalizeImages(record.articleImage).length} image{normalizeImages(record.articleImage).length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button variant="contained" size="small" onClick={() => fillForm(record)}>
                        Edit
                      </Button>
                      <Button variant="contained" color="error" size="small" onClick={() => handleDelete(record._id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <Dialog
        open={Boolean(viewingRecord)}
        onClose={() => !updatingImages && !uploading && setViewingRecord(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>{viewingRecord?.title || "Viral images"}</span>
          <Button
            component="label"
            variant="contained"
            startIcon={<span aria-hidden="true">+</span>}
            disabled={uploading || updatingImages || viewImages.length >= 10}
          >
            Add images
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => {
                addViewerImages(Array.from(event.target.files || []));
                event.target.value = "";
              }}
            />
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
            {viewImages.map((image, index) => (
              <div key={`${image}-${index}`} style={{ position: "relative", aspectRatio: "1 / 1" }}>
                <img
                  src={image}
                  alt={`Viral image ${index + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }}
                />
                <Tooltip title={viewImages.length === 1 ? "At least one image is required" : "Delete image"}>
                  <span>
                    <IconButton
                      aria-label={`Delete image ${index + 1}`}
                      onClick={() => persistViewerImages(viewImages.filter((_, imageIndex) => imageIndex !== index))}
                      disabled={updatingImages || uploading || viewImages.length <= 1}
                      sx={{ position: "absolute", top: 5, right: 5, bgcolor: "white", color: "error.main", "&:hover": { bgcolor: "#fff0f0" } }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            ))}
          </div>
          {(updatingImages || uploading) && <CircularProgress size={22} style={{ marginTop: 16 }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViralImages;
